import apiService from "./apiService";

// Default number of items per page
const DEFAULT_PAGE_SIZE = 50;

class FileManagerService {
  constructor() {
    this.CDN_URL = "https://s3file.s3.us-west-004.backblazeb2.com";
  }

  /**
   * Upload a file to the server
   * @param {FormData} formData - FormData containing the file
   * @param {Object} options - Upload options
   * @param {string} options.type - File type/category
   * @param {string} options.name - Original file name
   * @param {number} options.size - File size in bytes
   * @param {Function} options.onUploadProgress - Progress callback
   * @returns {Promise<Object>} - Uploaded file data
   */
  async uploadFile(
    formData,
    { type = "FILE", name = "file", size = 0, onUploadProgress } = {},
  ) {
    try {
      // Add metadata to form data
      formData.append("type", type);
      formData.append("originalName", name);
      formData.append("size", size);

      // Log what we're sending to the server
      console.log("Uploading file with formData keys:", [...formData.keys()]);

      const response = await apiService.request_old(
        "/filemanager/filem/upload",
        {
          method: "POST",
          body: formData,
          onUploadProgress: (progressEvent) => {
            if (onUploadProgress && progressEvent.lengthComputable) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onUploadProgress(percentCompleted);
            }
          },
        },
      );

      // Log the response for debugging
      console.log("File upload response:", response);

      // Ensure consistent response format
      if (response && response.data) {
        return response.data;
      }

      // If the response is the file data directly
      if (response && (response._id || response.id)) {
        return response;
      }

      // Check for other valid response formats
      if (response && response.url) {
        return response;
      }

      console.error("Unexpected response format:", response);
      throw new Error("Unexpected response format from server");
    } catch (error) {
      console.error("Error uploading file:", error);
      throw new Error(error.response?.data?.message || "Failed to upload file");
    }
  }

  // Upload file locally
  async uploadFileLocal(fileObj) {
    try {
      const response = await apiService.request_old("/public/file/upload", {
        method: "POST",
        body: fileObj, // FormData object
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // For Questions
  async uploadFileLocalTest(fileObj, endpoint) {
    try {
      const response = await apiService.request(endpoint, {
        method: "POST",
        body: fileObj, // FormData object
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  //For Solutions
  // async uploadFileLocalTestSolution(fileObj) {
  //   try {
  //     const response = await apiService.request("/test/upload/solution", {
  //       method: "POST",
  //       body: fileObj, // FormData object
  //     });
  //     return response;
  //   } catch (error) {
  //     throw error;
  //   }
  // }
  async uploadFileLocalTestSolution(fileObj, qSetId) {
    const response = await apiService.request(
      `/test/upload/solution?qSet=${encodeURIComponent(qSetId)}`,
      {
        method: "POST",
        body: fileObj,
      },
    );

    return response;
  }
  // Upload base64 file
  async uploadBase64File(fileObj) {
    try {
      const response = await apiService.request_old(
        "/public/file/base64-image",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(fileObj),
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get files with pagination and filtering
   * @param {Object} options - Pagination and filtering options
   * @param {number} options.page - Page number (1-based)
   * @param {number} options.limit - Items per page
   * @param {string} options.search - Search term
   * @param {string} options.type - File type filter
   * @param {string} options.sort - Sort field and direction (e.g., 'createdAt:desc')
   * @returns {Promise<{data: Array, total: number}>} - Files and total count
   */
  async getFiles({
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
    search = "",
    type = "",
    sort = "createdAt:desc",
  } = {}) {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: Math.max(1, parseInt(page)),
        limit: Math.min(100, Math.max(1, parseInt(limit))),
        ...(search && { search }),
        ...(type && { type }),
        ...(sort && { sort }),
        totalCount: "true", // Ensure we get the total count
      });

      const url = `/filemanager/files?${params}`;
      const response = await apiService.request_old(url, { method: "GET" });

      // Handle different response formats
      if (Array.isArray(response)) {
        return {
          data: response,
          total: response.length,
          page: 1,
          pageSize: response.length,
        };
      }

      // If response has data and total properties
      if (response && typeof response === "object") {
        return {
          data: response.data || response.items || [],
          total: response.total || response.count || 0,
          page: response.page || 1,
          pageSize: response.limit || response.pageSize || DEFAULT_PAGE_SIZE,
        };
      }

      // Fallback for unexpected response format
      return {
        data: [],
        total: 0,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      };
    } catch (error) {
      console.error("Error fetching files:", error);
      throw new Error("Failed to fetch files. Please try again.");
    }
  }

  // Get single file by ID
  async getOneFile(id) {
    try {
      const response = await apiService.request_old(
        `/filemanager/filem/${id}`,
        {
          method: "GET",
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a file by ID
   * @param {string} fileId - ID of the file to delete
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteFile(fileId) {
    if (!fileId) {
      throw new Error("File ID is required");
    }

    try {
      const response = await apiService.request_old(
        `/filemanager/filem/${fileId}`,
        {
          method: "DELETE",
        },
      );

      // Handle different response formats
      if (response && (response.success || response.deleted)) {
        return { success: true, id: fileId };
      }

      // If the API returns the deleted file
      if (response && response._id) {
        return { success: true, id: response._id };
      }

      // Fallback for unexpected response
      return { success: true, id: fileId };
    } catch (error) {
      console.error(`Error deleting file ${fileId}:`, error);
      throw new Error(error.response?.data?.message || "Failed to delete file");
    }
  }

  // Add file metadata
  async addFilem(fileInfo) {
    try {
      const response = await apiService.request_old("/filemanager/file", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(fileInfo),
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Update file metadata
  async updateFilem(fileId, fileInfo) {
    try {
      const response = await apiService.request_old(
        `/filemanager/file/${fileId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(fileInfo),
        },
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get upload URL for direct upload
  async getUploadUrl(filter = {}) {
    try {
      const params = new URLSearchParams(filter).toString();
      const url = `/file/find-b2-files${params ? `?${params}` : ""}`;

      const response = await apiService.request_old(url, {
        method: "GET",
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Delete upload URL
  async deleteUploadUrl(filter = {}) {
    try {
      const params = new URLSearchParams(filter).toString();
      const url = `/file/delete-file${params ? `?${params}` : ""}`;

      const response = await apiService.request_old(url, {
        method: "DELETE",
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get file type information
   * @param {string} fileName - File name or extension
   * @returns {Object} File type information
   */
  getFileTypeInfo(fileName) {
    // Ensure fileName is a string and handle empty values
    if (!fileName || typeof fileName !== "string") {
      console.log("Invalid fileName passed to getFileTypeInfo:", fileName);
      return {
        category: "UNKNOWN",
        type: "UNKNOWN",
        icon: "file",
        previewable: false,
        color: "#9CA3AF", // gray-400
      };
    }

    // Extract extension if a full filename is provided
    const extension = fileName.includes(".")
      ? fileName.split(".").pop().toUpperCase()
      : fileName.toUpperCase();

    const fileTypes = {
      // Images
      JPG: {
        category: "IMAGE",
        type: "JPG",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      JPEG: {
        category: "IMAGE",
        type: "JPG",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      PNG: {
        category: "IMAGE",
        type: "PNG",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      GIF: {
        category: "IMAGE",
        type: "GIF",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      WEBP: {
        category: "IMAGE",
        type: "WEBP",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      SVG: {
        category: "IMAGE",
        type: "SVG",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      BMP: {
        category: "IMAGE",
        type: "BMP",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },
      TIFF: {
        category: "IMAGE",
        type: "TIFF",
        icon: "file-image",
        previewable: true,
        color: "#3B82F6",
      },

      // Documents
      PDF: {
        category: "DOCUMENT",
        type: "PDF",
        icon: "file-pdf",
        previewable: true,
        color: "#EF4444",
      },
      DOC: {
        category: "DOCUMENT",
        type: "DOC",
        icon: "file-word",
        previewable: false,
        color: "#2563EB",
      },
      DOCX: {
        category: "DOCUMENT",
        type: "DOCX",
        icon: "file-word",
        previewable: false,
        color: "#2563EB",
      },
      TXT: {
        category: "DOCUMENT",
        type: "TXT",
        icon: "file-text",
        previewable: true,
        color: "#6B7280",
      },
      RTF: {
        category: "DOCUMENT",
        type: "RTF",
        icon: "file-text",
        previewable: false,
        color: "#6B7280",
      },
      ODT: {
        category: "DOCUMENT",
        type: "ODT",
        icon: "file-word",
        previewable: false,
        color: "#2563EB",
      },

      // Spreadsheets
      XLS: {
        category: "SPREADSHEET",
        type: "XLS",
        icon: "file-excel",
        previewable: false,
        color: "#10B981",
      },
      XLSX: {
        category: "SPREADSHEET",
        type: "XLSX",
        icon: "file-excel",
        previewable: false,
        color: "#10B981",
      },
      CSV: {
        category: "SPREADSHEET",
        type: "CSV",
        icon: "file-csv",
        previewable: true,
        color: "#10B981",
      },
      ODS: {
        category: "SPREADSHEET",
        type: "ODS",
        icon: "file-excel",
        previewable: false,
        color: "#10B981",
      },

      // Presentations
      PPT: {
        category: "PRESENTATION",
        type: "PPT",
        icon: "file-powerpoint",
        previewable: false,
        color: "#F59E0B",
      },
      PPTX: {
        category: "PRESENTATION",
        type: "PPTX",
        icon: "file-powerpoint",
        previewable: false,
        color: "#F59E0B",
      },
      ODP: {
        category: "PRESENTATION",
        type: "ODP",
        icon: "file-powerpoint",
        previewable: false,
        color: "#F59E0B",
      },

      // Archives
      ZIP: {
        category: "ARCHIVE",
        type: "ZIP",
        icon: "file-archive",
        previewable: false,
        color: "#8B5CF6",
      },
      RAR: {
        category: "ARCHIVE",
        type: "RAR",
        icon: "file-archive",
        previewable: false,
        color: "#8B5CF6",
      },
      "7Z": {
        category: "ARCHIVE",
        type: "7Z",
        icon: "file-archive",
        previewable: false,
        color: "#8B5CF6",
      },
      TAR: {
        category: "ARCHIVE",
        type: "TAR",
        icon: "file-archive",
        previewable: false,
        color: "#8B5CF6",
      },
      GZ: {
        category: "ARCHIVE",
        type: "GZ",
        icon: "file-archive",
        previewable: false,
        color: "#8B5CF6",
      },

      // Code
      JS: {
        category: "CODE",
        type: "JAVASCRIPT",
        icon: "file-code",
        previewable: true,
        color: "#F59E0B",
      },
      JSX: {
        category: "CODE",
        type: "JAVASCRIPT",
        icon: "file-code",
        previewable: true,
        color: "#F59E0B",
      },
      TS: {
        category: "CODE",
        type: "TYPESCRIPT",
        icon: "file-code",
        previewable: true,
        color: "#3B82F6",
      },
      TSX: {
        category: "CODE",
        type: "TYPESCRIPT",
        icon: "file-code",
        previewable: true,
        color: "#3B82F6",
      },
      HTML: {
        category: "CODE",
        type: "HTML",
        icon: "file-code",
        previewable: true,
        color: "#E44D26",
      },
      CSS: {
        category: "CODE",
        type: "CSS",
        icon: "file-code",
        previewable: true,
        color: "#264DE4",
      },
      SCSS: {
        category: "CODE",
        type: "SCSS",
        icon: "file-code",
        previewable: true,
        color: "#CC6699",
      },
      JSON: {
        category: "CODE",
        type: "JSON",
        icon: "file-code",
        previewable: true,
        color: "#1E88E5",
      },
      PY: {
        category: "CODE",
        type: "PYTHON",
        icon: "file-code",
        previewable: true,
        color: "#3776AB",
      },
      JAVA: {
        category: "CODE",
        type: "JAVA",
        icon: "file-code",
        previewable: true,
        color: "#007396",
      },
      PHP: {
        category: "CODE",
        type: "PHP",
        icon: "file-code",
        previewable: true,
        color: "#777BB4",
      },
      RB: {
        category: "CODE",
        type: "RUBY",
        icon: "file-code",
        previewable: true,
        color: "#CC342D",
      },
      GO: {
        category: "CODE",
        type: "GO",
        icon: "file-code",
        previewable: true,
        color: "#00ADD8",
      },
      RS: {
        category: "CODE",
        type: "RUST",
        icon: "file-code",
        previewable: true,
        color: "#000000",
      },
      C: {
        category: "CODE",
        type: "C",
        icon: "file-code",
        previewable: true,
        color: "#A8B9CC",
      },
      CPP: {
        category: "CODE",
        type: "CPP",
        icon: "file-code",
        previewable: true,
        color: "#00599C",
      },
      CS: {
        category: "CODE",
        type: "CSHARP",
        icon: "file-code",
        previewable: true,
        color: "#239120",
      },
      SWIFT: {
        category: "CODE",
        type: "SWIFT",
        icon: "file-code",
        previewable: true,
        color: "#FA7343",
      },
      KT: {
        category: "CODE",
        type: "KOTLIN",
        icon: "file-code",
        previewable: true,
        color: "#7F52FF",
      },
      DART: {
        category: "CODE",
        type: "DART",
        icon: "file-code",
        previewable: true,
        color: "#0175C2",
      },
      SH: {
        category: "CODE",
        type: "SHELL",
        icon: "terminal",
        previewable: true,
        color: "#4EAA25",
      },
      BAT: {
        category: "CODE",
        type: "BATCH",
        icon: "terminal",
        previewable: true,
        color: "#4EAA25",
      },
      PS1: {
        category: "CODE",
        type: "POWERSHELL",
        icon: "terminal",
        previewable: true,
        color: "#012456",
      },
      SQL: {
        category: "CODE",
        type: "SQL",
        icon: "database",
        previewable: true,
        color: "#336791",
      },
      MD: {
        category: "MARKDOWN",
        type: "MARKDOWN",
        icon: "markdown",
        previewable: true,
        color: "#000000",
      },
      YML: {
        category: "CONFIG",
        type: "YAML",
        icon: "file-code",
        previewable: true,
        color: "#CB171E",
      },
      YAML: {
        category: "CONFIG",
        type: "YAML",
        icon: "file-code",
        previewable: true,
        color: "#CB171E",
      },
      XML: {
        category: "MARKUP",
        type: "XML",
        icon: "file-code",
        previewable: true,
        color: "#F16529",
      },

      // Audio
      MP3: {
        category: "AUDIO",
        type: "MP3",
        icon: "file-audio",
        previewable: true,
        color: "#8B5CF6",
      },
      WAV: {
        category: "AUDIO",
        type: "WAV",
        icon: "file-audio",
        previewable: true,
        color: "#8B5CF6",
      },
      OGG: {
        category: "AUDIO",
        type: "OGG",
        icon: "file-audio",
        previewable: true,
        color: "#8B5CF6",
      },
      M4A: {
        category: "AUDIO",
        type: "M4A",
        icon: "file-audio",
        previewable: true,
        color: "#8B5CF6",
      },
      FLAC: {
        category: "AUDIO",
        type: "FLAC",
        icon: "file-audio",
        previewable: true,
        color: "#8B5CF6",
      },
      AAC: {
        category: "AUDIO",
        type: "AAC",
        icon: "file-audio",
        previewable: true,
        color: "#8B5CF6",
      },

      // Video
      MP4: {
        category: "VIDEO",
        type: "MP4",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      WEBM: {
        category: "VIDEO",
        type: "WEBM",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      MOV: {
        category: "VIDEO",
        type: "MOV",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      AVI: {
        category: "VIDEO",
        type: "AVI",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      WMV: {
        category: "VIDEO",
        type: "WMV",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      MKV: {
        category: "VIDEO",
        type: "MKV",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      FLV: {
        category: "VIDEO",
        type: "FLV",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      "3GP": {
        category: "VIDEO",
        type: "3GP",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      M4V: {
        category: "VIDEO",
        type: "M4V",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      MPG: {
        category: "VIDEO",
        type: "MPG",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },
      MPEG: {
        category: "VIDEO",
        type: "MPEG",
        icon: "file-video",
        previewable: true,
        color: "#EC4899",
      },

      // Fonts
      TTF: {
        category: "FONT",
        type: "TTF",
        icon: "font",
        previewable: false,
        color: "#8B5CF6",
      },
      OTF: {
        category: "FONT",
        type: "OTF",
        icon: "font",
        previewable: false,
        color: "#8B5CF6",
      },
      WOFF: {
        category: "FONT",
        type: "WOFF",
        icon: "font",
        previewable: false,
        color: "#8B5CF6",
      },
      WOFF2: {
        category: "FONT",
        type: "WOFF2",
        icon: "font",
        previewable: false,
        color: "#8B5CF6",
      },
      EOT: {
        category: "FONT",
        type: "EOT",
        icon: "font",
        previewable: false,
        color: "#8B5CF6",
      },

      // 3D Models
      GLB: {
        category: "3D",
        type: "GLB",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      GLTF: {
        category: "3D",
        type: "GLTF",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      FBX: {
        category: "3D",
        type: "FBX",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      OBJ: {
        category: "3D",
        type: "OBJ",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      STL: {
        category: "3D",
        type: "STL",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      DAE: {
        category: "3D",
        type: "COLLADA",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      BLEND: {
        category: "3D",
        type: "BLENDER",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      "3DS": {
        category: "3D",
        type: "3DS",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      MAX: {
        category: "3D",
        type: "MAX",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      MA: {
        category: "3D",
        type: "MAYA",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },
      MB: {
        category: "3D",
        type: "MAYA",
        icon: "box",
        previewable: false,
        color: "#8B5CF6",
      },

      // Vector Graphics
      AI: {
        category: "VECTOR",
        type: "AI",
        icon: "vector-square",
        previewable: false,
        color: "#FF9A00",
      },
      EPS: {
        category: "VECTOR",
        type: "EPS",
        icon: "vector-square",
        previewable: false,
        color: "#FF9A00",
      },
      SVG: {
        category: "VECTOR",
        type: "SVG",
        icon: "vector-square",
        previewable: true,
        color: "#FF9A00",
      },
      PSD: {
        category: "VECTOR",
        type: "PSD",
        icon: "image",
        previewable: false,
        color: "#31A8FF",
      },
      SKETCH: {
        category: "VECTOR",
        type: "SKETCH",
        icon: "pen-tool",
        previewable: false,
        color: "#FFAE00",
      },
      XD: {
        category: "VECTOR",
        type: "XD",
        icon: "pen-tool",
        previewable: false,
        color: "#FF61F6",
      },
      FIG: {
        category: "VECTOR",
        type: "FIGMA",
        icon: "figma",
        previewable: false,
        color: "#A259FF",
      },
      SKP: {
        category: "3D",
        type: "SKETCHUP",
        icon: "box",
        previewable: false,
        color: "#005F9E",
      },
    };

    return (
      fileTypes[extension] || {
        category: "FILE",
        type: extension || "UNKNOWN",
        icon: "file",
        previewable: false,
        color: "#9CA3AF", // gray-400
      }
    );
  }

  /**
   * Validate if a file type is allowed
   * @param {File|string} file - File object or file name/extension
   * @param {string[]} allowedTypes - Array of allowed file extensions (without dot)
   * @returns {boolean} - True if file type is allowed
   */
  isValidFileType(file, allowedTypes = []) {
    if (!allowedTypes.length) return true;

    // Handle both File objects and string file names
    const fileName = typeof file === "string" ? file : file.name;
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    // Check if the extension is in the allowed types (case insensitive)
    return allowedTypes.some(
      (type) => type.toLowerCase() === extension.toLowerCase(),
    );
  }

  /**
   * Get the file extension from a file name or path
   * @param {string} fileName - File name or path
   * @returns {string} - File extension in lowercase (without dot)
   */
  getFileExtension(fileName) {
    if (!fileName) return "";
    return fileName.split(".").pop().toLowerCase();
  }

  /**
   * Check if a file is an image
   * @param {File|string} file - File object or file name/extension
   * @returns {boolean} - True if the file is an image
   */
  isImage(file) {
    const typeInfo = this.getFileTypeInfo(file);
    return typeInfo.category === "IMAGE" || typeInfo.category === "VECTOR";
  }

  /**
   * Check if a file can be previewed in the browser
   * @param {File|string} file - File object or file name/extension
   * @returns {boolean} - True if the file can be previewed
   */
  isPreviewable(file) {
    const typeInfo = this.getFileTypeInfo(file);
    return typeInfo.previewable === true;
  }

  /**
   * Format file size in a human-readable format
   * @param {number} bytes - File size in bytes
   * @param {number} decimals - Number of decimal places (default: 2)
   * @returns {string} - Formatted file size (e.g., '1.5 MB')
   */
  formatFileSize(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // Handle very small files (less than 1 KB)
    if (i === 0) return `${bytes} ${sizes[i]}`;

    // Format the number with the specified decimal places
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

    // Remove trailing .00 if not needed
    const formattedSize =
      size % 1 === 0 ? size.toString() : size.toFixed(decimals);

    return `${formattedSize} ${sizes[i]}`;
  }

  /**
   * Generate a thumbnail URL for a file
   * @param {Object|string} file - File object or file URL
   * @param {Object} options - Thumbnail options
   * @param {number} options.width - Thumbnail width (default: 300)
   * @param {number} options.height - Thumbnail height (default: 300)
   * @param {string} options.fit - Fit mode: 'cover', 'contain', 'fill', 'inside', 'outside' (default: 'cover')
   * @returns {string} - Thumbnail URL
   */
  getThumbnailUrl(file, { width = 300, height = 300, fit = "cover" } = {}) {
    if (!file) return "";

    // Handle both file objects with URL property and direct URLs
    const fileUrl = typeof file === "string" ? file : file.url || "";
    if (!fileUrl) return "";

    // If it's already a thumbnail URL, return as is
    if (fileUrl.includes("?width=") || fileUrl.includes("?h=")) {
      return fileUrl;
    }

    // For images, we can generate a thumbnail URL
    if (this.isImage(file)) {
      const url = new URL(fileUrl, window.location.origin);
      url.searchParams.set("width", width);
      url.searchParams.set("height", height);
      url.searchParams.set("fit", fit);
      return url.toString();
    }

    // For non-image files, return the original URL
    return fileUrl;
  }
}

export default new FileManagerService();
